import { gql, useMutation } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@/gf/lib/yupResolver'
import SelectField from '@/gf/components/SelectField'
import ModalForm from '@/gf/components/ModalForm'
import useMsgs from '@/gf/hooks/useMsgs'

const ADD_OFFLINE_BUYER_TRANSACTION = gql`
  mutation AddOfflineBuyerTransaction(
    $paymentMethod: OfflineTransactionPaymentMethod
    $storeOrderId: ID!
  ) {
    AddOfflineBuyerTransaction(paymentMethod: $paymentMethod, storeOrderId: $storeOrderId) {
      id
    }
  }
`
type OfflineBuyerTransactionForm = {
  paymentMethod: 'stripe' | 'wire' | 'enterprise'
}
const offlineBuyerTransactionSchema = yup.object({
  paymentMethod: yup.string().oneOf(['stripe', 'wire', 'enterprise']).required(),
})

const failureMessage = 'Error adding offline charge transaction'

const OfflineBuyerTransactionModalForm = ({
  onClose,
  onSubmit: _onSubmit,
  open,
  storeOrderId,
}: {
  storeOrderId: string
  onClose: () => void
  onSubmit: () => void
  open: boolean
}) => {
  const offlineBuyerTransactionForm = useForm<OfflineBuyerTransactionForm>({
    defaultValues: {
      paymentMethod: 'stripe',
    },
    resolver: yupResolver(offlineBuyerTransactionSchema),
  })
  const selectedPaymentMethod = useWatch({
    control: offlineBuyerTransactionForm.control,
    name: 'paymentMethod',
  })
  // The modal stays mounted, so clear the form on close: the next open starts from the defaults.
  // Do not use `shouldUnregister` for this: the select is not a registered field, and
  // react-hook-form 7.89 then leaves it out of getValues() (GEA-11417).
  const close = () => {
    offlineBuyerTransactionForm.reset()
    onClose()
  }

  const [addOfflineBuyerTransaction] = useMutation(ADD_OFFLINE_BUYER_TRANSACTION)
  const [_msgs, msgsMgr] = useMsgs()
  return (
    <ModalForm
      title="Add Offline Charge"
      onClose={close}
      onSubmit={(e) => {
        e.preventDefault()

        function mapFormToGqlVariables(form: OfflineBuyerTransactionForm) {
          return {
            ...form,
            storeOrderId,
            paymentMethod: form.paymentMethod.toLocaleUpperCase(),
          }
        }

        const form = offlineBuyerTransactionForm.getValues()
        const variables = mapFormToGqlVariables(form)

        addOfflineBuyerTransaction({ variables })
          .then(() => {
            msgsMgr.add('Successfully added offline buyer transaction!', 'positive')
            close()
          })
          .catch((err: GraphQLError) => {
            msgsMgr.add(err.message || failureMessage, 'negative')
          })
      }}
      open={open}
    >
      <div className="mb-8 pt-4">
        <SelectField
          label="Payment Method"
          required
          options={[
            { id: 'stripe', display: 'Stripe' },
            { id: 'wire', display: 'Wire' },
            { id: 'enterprise', display: 'Enterprise' },
          ]}
          currentId={selectedPaymentMethod}
          onChange={(selectedId) => {
            if (selectedId === 'stripe' || selectedId === 'wire' || selectedId === 'enterprise') {
              offlineBuyerTransactionForm.setValue('paymentMethod', selectedId)
            }
          }}
        />
      </div>
    </ModalForm>
  )
}

export default OfflineBuyerTransactionModalForm
